import { useStatsigClient } from '@statsig/react-bindings';
import { useSubscription } from '../contexes/SubscriptionProvider';
import { canAccessIntegrations } from '../helpers/integrationsAccess';
import { FeatureGate } from '../interfaces/FeatureGate';
import useAuth from './useAuth';

function isStatsigClientReady(client: {
  isNoop?: boolean;
  loadingStatus?: string;
}): boolean {
  if ('isNoop' in client && client.isNoop) {
    return false;
  }
  // StatsigProvider only mounts children once Ready, but guard anyway.
  if (client.loadingStatus && client.loadingStatus !== 'Ready') {
    return false;
  }
  return true;
}

function checkIntegrationsGate(client: {
  checkGate?: (gate: FeatureGate) => boolean;
}): boolean {
  try {
    return Boolean(client.checkGate?.(FeatureGate.INTEGRATIONS));
  } catch (err) {
    console.error(
      `Error checking feature gate ${FeatureGate.INTEGRATIONS}:`,
      err
    );
    return false;
  }
}

/**
 * Integrations require Statsig + company plan + manage_integrations (or admin /
 * Owner). Outside StatsigProvider the SDK returns a Noop client — wait for a
 * real ready client before denying route access (avoids refresh → 404).
 *
 * Gate is checked synchronously so the Integrations page does not redirect to
 * 404 on the first paint while async useIsFeatureEnabled is still false.
 */
export function useIntegrationsAccess(): {
  canUse: boolean;
  isReady: boolean;
} {
  const { currentUser } = useAuth();
  const { company } = useSubscription();
  const { client } = useStatsigClient();
  const isReady = isStatsigClientReady(client);
  const isIntegrationsFeatureEnabled = isReady && checkIntegrationsGate(client);
  const canUse =
    isIntegrationsFeatureEnabled && canAccessIntegrations(company, currentUser);

  return { canUse, isReady };
}
